import { supabase } from '../lib/supabase';

export const clientService = {
  // Get all clients for a company
  async getCompanyClients(companyId, includeInactive = false) {
    let query = supabase
      ?.from('clients')
      ?.select('*')
      ?.eq('company_id', companyId)
      ?.order('client_name', { ascending: true });

    if (!includeInactive) {
      query = query?.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(client => ({
      id: client?.id,
      companyId: client?.company_id,
      clientName: client?.client_name,
      contactPerson: client?.contact_person,
      contactEmail: client?.contact_email,
      contactPhone: client?.contact_phone,
      address: client?.address,
      addressLat: client?.address_lat,
      addressLng: client?.address_lng,
      abn: client?.abn,
      tag: client?.tag,
      notes: client?.notes,
      isActive: client?.is_active,
      createdAt: client?.created_at,
      updatedAt: client?.updated_at
    }));
  },

  // Search clients by name
  async searchClients(companyId, searchTerm) {
    const { data, error } = await supabase
      ?.from('clients')
      ?.select('*')
      ?.eq('company_id', companyId)
      ?.eq('is_active', true)
      ?.ilike('client_name', `%${searchTerm}%`)
      ?.order('client_name', { ascending: true })
      ?.limit(10);

    if (error) throw error;

    return data?.map(client => ({
      id: client?.id,
      companyId: client?.company_id,
      clientName: client?.client_name,
      contactPerson: client?.contact_person,
      contactEmail: client?.contact_email,
      contactPhone: client?.contact_phone,
      address: client?.address,
      addressLat: client?.address_lat,
      addressLng: client?.address_lng,
      abn: client?.abn,
      tag: client?.tag,
      notes: client?.notes,
      isActive: client?.is_active
    }));
  },

  // Get a single client by ID
  async getClient(clientId) {
    const { data, error } = await supabase
      ?.from('clients')
      ?.select('*')
      ?.eq('id', clientId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      clientName: data?.client_name,
      contactPerson: data?.contact_person,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      address: data?.address,
      addressLat: data?.address_lat,
      addressLng: data?.address_lng,
      abn: data?.abn,
      tag: data?.tag,
      notes: data?.notes,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Get a client with its job sites
  async getClientWithJobSites(clientId) {
    const { data, error } = await supabase
      ?.from('clients')
      ?.select(`
        *,
        job_sites (
          id,
          site_name,
          address,
          address_lat,
          address_lng,
          contact_person,
          contact_email,
          contact_phone,
          notes,
          is_active,
          created_at,
          updated_at
        )
      `)
      ?.eq('id', clientId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      clientName: data?.client_name,
      contactPerson: data?.contact_person,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      address: data?.address,
      addressLat: data?.address_lat,
      addressLng: data?.address_lng,
      abn: data?.abn,
      tag: data?.tag,
      notes: data?.notes,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      jobSites: data?.job_sites?.map(site => ({
        id: site?.id,
        siteName: site?.site_name,
        address: site?.address,
        addressLat: site?.address_lat,
        addressLng: site?.address_lng,
        contactPerson: site?.contact_person,
        contactEmail: site?.contact_email,
        contactPhone: site?.contact_phone,
        notes: site?.notes,
        isActive: site?.is_active,
        createdAt: site?.created_at,
        updatedAt: site?.updated_at
      }))
    };
  },

  // Get all clients with their job sites for a company
  async getClientsWithJobSites(companyId, includeInactive = false) {
    let query = supabase
      ?.from('clients')
      ?.select(`
        *,
        job_sites (
          id,
          site_name,
          site_tag,
          address,
          address_lat,
          address_lng,
          contact_person,
          contact_email,
          contact_phone,
          notes,
          is_active,
          created_at,
          updated_at
        )
      `)
      ?.eq('company_id', companyId)
      ?.order('client_name', { ascending: true });

    if (!includeInactive) {
      query = query?.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(client => ({
      id: client?.id,
      companyId: client?.company_id,
      clientName: client?.client_name,
      contactPerson: client?.contact_person,
      contactEmail: client?.contact_email,
      contactPhone: client?.contact_phone,
      address: client?.address,
      addressLat: client?.address_lat,
      addressLng: client?.address_lng,
      abn: client?.abn,
      tag: client?.tag,
      notes: client?.notes,
      isActive: client?.is_active,
      createdAt: client?.created_at,
      updatedAt: client?.updated_at,
      jobSites: client?.job_sites
        ?.filter(site => includeInactive || site?.is_active)
        ?.map(site => ({
          id: site?.id,
          siteName: site?.site_name,
          siteTag: site?.site_tag,
          address: site?.address,
          addressLat: site?.address_lat,
          addressLng: site?.address_lng,
          contactPerson: site?.contact_person,
          contactEmail: site?.contact_email,
          contactPhone: site?.contact_phone,
          notes: site?.notes,
          isActive: site?.is_active,
          createdAt: site?.created_at,
          updatedAt: site?.updated_at
        }))
    }));
  },

  // Create a new client
  async createClient(clientData, companyId) {
    const { data, error } = await supabase
      ?.from('clients')
      ?.insert({
        company_id: companyId,
        client_name: clientData?.clientName,
        contact_person: clientData?.contactPerson,
        contact_email: clientData?.contactEmail,
        contact_phone: clientData?.contactPhone,
        address: clientData?.address,
        address_lat: clientData?.addressLat,
        address_lng: clientData?.addressLng,
        abn: clientData?.abn,
        tag: clientData?.tag,
        notes: clientData?.notes
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      clientName: data?.client_name,
      contactPerson: data?.contact_person,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      address: data?.address,
      addressLat: data?.address_lat,
      addressLng: data?.address_lng,
      abn: data?.abn,
      tag: data?.tag,
      notes: data?.notes,
      isActive: data?.is_active,
      createdAt: data?.created_at
    };
  },

  // Update a client
  async updateClient(clientId, updates) {
    const updateData = {};

    if (updates?.clientName !== undefined) updateData.client_name = updates.clientName;
    if (updates?.contactPerson !== undefined) updateData.contact_person = updates.contactPerson;
    if (updates?.contactEmail !== undefined) updateData.contact_email = updates.contactEmail;
    if (updates?.contactPhone !== undefined) updateData.contact_phone = updates.contactPhone;
    if (updates?.address !== undefined) updateData.address = updates.address;
    if (updates?.addressLat !== undefined) updateData.address_lat = updates.addressLat;
    if (updates?.addressLng !== undefined) updateData.address_lng = updates.addressLng;
    if (updates?.abn !== undefined) updateData.abn = updates.abn;
    if (updates?.tag !== undefined) updateData.tag = updates.tag;
    if (updates?.notes !== undefined) updateData.notes = updates.notes;
    if (updates?.isActive !== undefined) updateData.is_active = updates.isActive;

    const { data, error } = await supabase
      ?.from('clients')
      ?.update(updateData)
      ?.eq('id', clientId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      clientName: data?.client_name,
      contactPerson: data?.contact_person,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      address: data?.address,
      addressLat: data?.address_lat,
      addressLng: data?.address_lng,
      abn: data?.abn,
      tag: data?.tag,
      notes: data?.notes,
      isActive: data?.is_active,
      updatedAt: data?.updated_at
    };
  },

  // Deactivate a client (soft delete)
  async deactivateClient(clientId) {
    const { error } = await supabase
      ?.from('clients')
      ?.update({ is_active: false })
      ?.eq('id', clientId);

    if (error) throw error;
  },

  // Reactivate a client
  async reactivateClient(clientId) {
    const { error } = await supabase
      ?.from('clients')
      ?.update({ is_active: true })
      ?.eq('id', clientId);

    if (error) throw error;
  },

  // Delete a client (hard delete - use with caution)
  async deleteClient(clientId) {
    const { error } = await supabase
      ?.from('clients')
      ?.delete()
      ?.eq('id', clientId);

    if (error) throw error;
  },

  // Get jobs for a client
  async getClientJobs(clientId) {
    const { data, error } = await supabase
      ?.from('jobs')
      ?.select(`
        *,
        job_site:job_sites(id, site_name, address),
        created_by_profile:user_profiles!jobs_created_by_fkey(id, full_name, email)
      `)
      ?.eq('client_id', clientId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(job => ({
      id: job?.id,
      jobDisplayId: job?.job_display_id,
      jobName: job?.job_name,
      description: job?.description,
      status: job?.status,
      priority: job?.priority,
      jobSite: job?.job_site ? {
        id: job?.job_site?.id,
        siteName: job?.job_site?.site_name,
        address: job?.job_site?.address
      } : null,
      createdAt: job?.created_at,
      createdByProfile: job?.created_by_profile
    }));
  },

  // Get job count for a client
  async getClientJobCount(clientId) {
    const { count, error } = await supabase
      ?.from('jobs')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('client_id', clientId);

    if (error) throw error;

    return count || 0;
  },

  // Get job site count for a client
  async getClientJobSiteCount(clientId, includeInactive = false) {
    let query = supabase
      ?.from('job_sites')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('client_id', clientId);

    if (!includeInactive) {
      query = query?.eq('is_active', true);
    }

    const { count, error } = await query;

    if (error) throw error;

    return count || 0;
  }
};
